import { ChangeDetectionStrategy, Component, EventEmitter, Output, ChangeDetectorRef, Input } from '@angular/core'
import { FormControl } from '@angular/forms'
import { AuthService } from 'src/app/singleton-services/auth/auth.service'
import { ViewAsService } from 'src/app/singleton-services/view-as.service'
import { CookieService } from 'src/app/singleton-services/cookie.service'
import { BehaviorSubject, of } from 'rxjs'
import { catchError } from 'rxjs/operators'

@Component({
  selector: 'flo-view-as-widget',
  styleUrls: ['./view-as-widget.component.scss'],
  templateUrl: './view-as-widget.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ViewAsWidgetComponent {
  @Output() acknowledged = new EventEmitter<void>()
  @Input() isViewingAsUser$ = new BehaviorSubject<boolean>(false)
  @Input() viewedUserInfo$ = new BehaviorSubject<any>(null)

  public expanded = false
  public hasAcknowledgedWarning = false
  viewAsUserControl = new FormControl('')
  viewAsError$ = new BehaviorSubject<string | null>(null)
  private readonly ORIGINAL_USER_STORAGE_KEY = 'flo_original_user_tokens'

  constructor(
    private cd: ChangeDetectorRef,
    private auth: AuthService,
    private viewAsService: ViewAsService,
    private cookieService: CookieService
  ) {}

  closeWindow() {
    this.expanded = false
    this.hasAcknowledgedWarning = false
    this.viewAsUserControl.setValue('')
    this.viewAsError$.next(null)
    this.cd.detectChanges()
  }

  openWindow() {
    this.expanded = true
    this.cd.detectChanges()
  }

  toggleWindow() {
    this.expanded = !this.expanded
    this.cd.detectChanges()
  }

  onAcknowledgeWarning() {
    this.hasAcknowledgedWarning = true
    this.cd.detectChanges()
  }

  viewAsUser() {
    const userInput = this.viewAsUserControl.value

    if (!userInput || !userInput.trim()) {
      this.viewAsError$.next('Please enter a user email or ID')
      return
    }

    this.viewAsError$.next(null)

    // Save current admin tokens before switching
    this.saveOriginalUserTokens()

    this.auth
      .getUserTokens(userInput.trim())
      .pipe(
        catchError((error: Error) => {
          this.viewAsError$.next(error.message || 'User not found')
          // Clear saved tokens if switching fails
          this.clearOriginalUserTokens()
          return of(null)
        })
      )
      .subscribe(userData => {
        if (userData?.jwt_token && userData?.refresh_token) {
          // Store viewed user information
          const viewedUser = {
            email: userData.email || userInput.trim(),
            id: userData.id,
            name:
              userData.first_name && userData.last_name
                ? `${userData.first_name} ${userData.last_name}`.trim()
                : undefined
          }
          this.viewAsService.setViewingAsUser(viewedUser)

          // Both tokens are required for proper user switching
          this.auth.setToken(userData.jwt_token).subscribe(() => {
            // Calculate expiry time - default to 30 days from now
            const refreshTokenExpiry = Math.floor(Date.now() / 1000) + 30 * 24 * 60 * 60

            this.auth.setRefreshToken(userData.refresh_token as string, refreshTokenExpiry).subscribe(() => {
              this.viewAsUserControl.setValue('')
              this.viewAsError$.next(null)
              this.closeWindow()
              // Reload to apply the new user context
              window.location.reload()
            })
          })
        } else if (userData?.jwt_token && !userData?.refresh_token) {
          this.viewAsError$.next('Refresh token not provided. Cannot switch user context.')
          // Clear saved tokens if switching fails
          this.clearOriginalUserTokens()
        } else {
          this.viewAsError$.next('No JWT token returned. Admin impersonation may not be enabled.')
          // Clear saved tokens if switching fails
          this.clearOriginalUserTokens()
        }
      })
  }

  stopViewingAsUser() {
    const originalTokens = this.getOriginalUserTokens()

    if (!originalTokens) {
      this.viewAsError$.next('No original user session found')
      return
    }

    // Restore original admin tokens
    this.auth.setToken(originalTokens.jwt_token).subscribe(() => {
      // Use a default expiration for the refresh token (30 days from now)
      // The actual validation happens server-side
      const refreshTokenExpiry = Math.floor(Date.now() / 1000) + 30 * 24 * 60 * 60
      this.auth.setRefreshToken(originalTokens.refresh_token, refreshTokenExpiry).subscribe(() => {
        // Clear the view-as state
        this.viewAsService.clearViewingAsUser()
        this.viewAsError$.next(null)
        this.closeWindow()
        // Reload to apply the original user context
        window.location.reload()
      })
    })
  }

  private saveOriginalUserTokens() {
    const currentJwtToken = this.auth.getTokenFromStore()
    const currentRefreshToken = this.auth.getRefreshTokenFromStore()

    if (currentJwtToken && currentRefreshToken) {
      const tokenData = {
        jwt_token: currentJwtToken,
        refresh_token: currentRefreshToken,
        saved_at: new Date().toISOString()
      }

      this.cookieService.set(this.ORIGINAL_USER_STORAGE_KEY, JSON.stringify(tokenData), {
        expires: new Date(Date.now() + 3600000), // 1 hour
        path: '/'
      })
    }
  }

  private getOriginalUserTokens(): any {
    const stored = this.cookieService.get(this.ORIGINAL_USER_STORAGE_KEY)
    return stored ? (typeof stored === 'string' ? JSON.parse(stored) : stored) : null
  }

  private clearOriginalUserTokens() {
    this.cookieService.remove(this.ORIGINAL_USER_STORAGE_KEY)
  }
}
